import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { IProctorGroups } from "../models/proctor-stuff"
import { selectorOf } from "./redux-helpers"
import { run, typeOf } from "./utils"

export type Environment = "UNKNOWN" | "LOCAL" | "QA" | "PRODUCTION"
export type IConfig = typeof defaultConfig

export const defaultConfig = {
    env: "UNKNOWN" as Environment,
    myJobsUrl: "https://myjobs.indeed.com",
    profileUrl: "https://profile.indeed.com",
    apiBase: "/invitations/api",
    jobSearchUrl: "https://indeed.com",
    appBase: "/invitations",
    feedbackThanksUrl: "/invitations/feedback/thanks",
    jobMatchBaseUrl: "/job-match",
    jobMatchApiBaseUrl: "/job-match/api",
    jobMatchThanksUrl: "/job-match/thanks",
    pauseUrl: "/invitations/pause",
    errorUrl: "/error",
    proctorGroups: {} as IProctorGroups,
    trackingKey: "tk",
    maxCarouselJobs: 5,
    updatePreferencesUrl: "https://secure.indeed.com/settings/notifications",
    maxFeedbackTextLength: 500,
}

export const configSlice = createSlice({
    name: "config",
    initialState: getConfig(),
    reducers: {
        setTrackingKey(state, action: PayloadAction<string>) {
            state.trackingKey = action.payload
        },
        // used to set proctor groups in stories
        setProctorGroups(state, action: PayloadAction<Record<string, number>>) {
            state.proctorGroups = {
                ...state.proctorGroups,
                ...action.payload,
            }
        },
    },
})

export const configSelector = selectorOf((s) => s.config)
export const configActions = configSlice.actions

function getForcedProctorGroups(debugBarObjName = "__debugBarData"): Record<string, number> {
    // @ts-ignore
    const prForcedGroups = (window[debugBarObjName]?.forcedGroups ?? []) as string[]
    const res = {} as Record<string, number>

    prForcedGroups.forEach((it) => {
        // regex to get last line of number (including negatives). E.g. my_tst-1 or some_tst123
        const idx = it.match(/-?\d+$/)?.index ?? -1
        const tstName = it.slice(0, idx)
        const bucket = Number.parseInt(it.slice(idx), 10)
        res[tstName] = Number.isNaN(bucket) ? -1 : bucket
    })

    return res
}

/**
 * fetches a global config embedded in the template on the window object
 */
function getConfig(): IConfig {
    const maybeConfig: IConfig =
        // eslint-disable-next-line no-underscore-dangle
        window.__appConfig ??
        run(() => {
            console.warn("no global config found. Using defaultConfig")
            return defaultConfig
        })

    // add forced proctor groups
    const config = { ...maybeConfig, proctorGroups: { ...maybeConfig.proctorGroups, ...getForcedProctorGroups() } }

    // generate console warnings if something's wrong
    validateObject(defaultConfig, config)

    return config
}

function validateObject(defaultObject: Record<string, any>, objectToValidate: Record<string, any>): boolean {
    return Object.keys(defaultObject)
        .map((key) => {
            // TODO: this runtime type check is kinda buggy. It can't handle things like string | null
            const expected = typeOf(defaultObject[key])
            const actual = typeOf(objectToValidate[key])
            const isValid = actual === expected
            if (!isValid) {
                console.warn(`invalid object. Key with name "${key}" expected type ${expected} but got type ${actual}`)
            }
            return isValid
        })
        .every(Boolean)
}
